import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Typography, Flex, Button, Spin } from "antd"
import styles from "./index.module.css"
import PhoneIcon from "../../../../../../assets/icons/Phone.svg"
import MessageIcon from "../../../../../../assets/icons/messages.svg"
import GuestIcon from "../../../../../../assets/icons/guest.svg"
import CalendarIcon from "../../../../../../assets/icons/calendar.svg"
import { useDispatch, useSelector } from "react-redux"
import { getWaiverSubmissionById } from "store/digital-waiver"
const { Title, Text } = Typography

export const WaiverDetailsModal = ({ isShowLoading, setIsShowLoading, currentDocumentIndex, setCurrentDocumentIndex, record, open, onCancel }) => {
  const dispatch = useDispatch()
  const { data: submissionData, loading: submissionLoading } = useSelector((state) => state.digitalWaiver.currentWaiverSubmission)
  const [pdfUrls, setPdfUrls] = useState([])

  // Fetch waiver submission by ID when modal opens
  useEffect(() => {
    const submissionId = record?.waiver_submission_id || record?.id
if (open && submissionId) {
      dispatch(getWaiverSubmissionById(submissionId))
    }
  }, [open, record?.waiver_submission_id, record?.id, dispatch])

  // Build PDF blob URLs from the fetched submission data
  useEffect(() => {
    if (!submissionData) return

    const fromDocs = Array.isArray(submissionData?.documents)
      ? submissionData.documents.map((d) => d?.signed_pdf_url).filter(Boolean)
      : []

    const fromArrays = [
      ...(Array.isArray(submissionData?.signed_documents) ? submissionData.signed_documents : []),
      ...(Array.isArray(submissionData?.unsigned_documents) ? submissionData.unsigned_documents : [])
    ]

    const urlList = fromDocs.length > 0 ? fromDocs : fromArrays

    if (urlList.length === 0) {
      setPdfUrls([])
      setIsShowLoading(false)
      return
    }

    let createdUrls = []
    const fetchDocs = async () => {
      try {
        const blobs = await Promise.all(
          urlList.map(async (url) => {
            const res = await fetch(url)
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const blob = await res.blob()
            return URL.createObjectURL(blob)
          })
        )
        createdUrls = blobs
        setPdfUrls(blobs)
        setIsShowLoading(false)
      } catch (err) {

        setPdfUrls(urlList)
        setIsShowLoading(false)
      }
    }
    fetchDocs()
    return () => createdUrls.forEach((u) => URL.revokeObjectURL(u))
  }, [submissionData, setIsShowLoading])

  const totalCount = pdfUrls.length
  const currentPdfUrl = pdfUrls[currentDocumentIndex]

  const formatDate = (val) => {
    if (!val) return ""
    const d = new Date(val)
    return isNaN(d) ? val : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
  }

  const displayName = record?.full_name || record?.name || ""
  const email = record?.guest_email || record?.notify_details?.email || ""
  const phone = record?.phone_number || record?.notify_details?.phone_number || ""
  const birthdate = formatDate(record?.birthdate)

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
      centered
      className={styles.waiverModal}
      maskClosable={false}
      title={
        <div>
          <Title level={3} className={`${styles.title} mb-0`}>Waiver</Title>
          {displayName && <Text className={styles.subtitle}>{displayName}</Text>}
        </div>
      }
    >
      <Spin spinning={isShowLoading || submissionLoading}>
        <div className="p-xl">

          {/* Personal Information */}
          {displayName && (
            <div className="mb-l">
              <Flex justify="space-between" wrap="wrap" gap={8}>
                <Flex vertical gap={8}>
                  <Flex align="center" gap="small">
                    <img src={GuestIcon} alt="guest" />
                    <Text className={styles.infoText}>{displayName}</Text>
                  </Flex>
                  {birthdate && (
                    <Flex align="center" gap="small">
                      <img src={CalendarIcon} alt="birthdate" style={{ width: 16, height: 16 }} />
                      <Text className={styles.infoText}>{birthdate}</Text>
                    </Flex>
                  )}
                </Flex>
                <Flex vertical gap={8}>
                  {phone && (
                    <Flex align="center" gap="small">
                      <img src={PhoneIcon} alt="phone" />
                      <Text className={styles.infoText}>{phone}</Text>
                    </Flex>
                  )}
                  {email && (
                    <Flex align="center" gap="small">
                      <img src={MessageIcon} alt="email" />
                      <Text className={styles.infoText}>{email}</Text>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </div>
          )}

          {/* Document Preview */}
          <div className="mb-l">
            <Title level={4} className={`${styles.documentTitle} mb-m`}>
              Document Preview
            </Title>
            {totalCount > 0 && (
              <>
                <Flex align="center" justify="end" className="mb-xs">
                  <Text type="secondary">{currentDocumentIndex + 1} of {totalCount} documents</Text>
                </Flex>
                <div className={styles.pdfPreviewContainer}>
                  {currentPdfUrl && (
                    <iframe
                      src={`${currentPdfUrl}#toolbar=0&navpanes=0&zoom=page-width`}
                      className={styles.pdfObject}
                      title={`Document ${currentDocumentIndex + 1}`}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <Flex justify="end" gap={12}>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setCurrentDocumentIndex(currentDocumentIndex - 1)}
              disabled={currentDocumentIndex === 0}
            >
              Previous
            </Button>
            <Button
              color="primary"
              variant="solid"
              onClick={() => setCurrentDocumentIndex(currentDocumentIndex + 1)}
              disabled={currentDocumentIndex >= totalCount - 1}
            >
              Next
            </Button>
          </Flex>
        </div>
      </Spin>
    </Modal>
  )
}

WaiverDetailsModal.propTypes = {
  currentDocumentIndex: PropTypes.number.isRequired,
  setCurrentDocumentIndex: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired
}
